const AUTH_CONFIG_URL = '/admin/cms/auth-config';
const GOOGLE_AUTH_URL = '/admin/cms/google-auth';
const GOOGLE_LOCALE = 'en';
const GSI_SCRIPT_URL = `https://accounts.google.com/gsi/client?hl=${GOOGLE_LOCALE}`;

/** @typedef {{ googleAuthEnabled: boolean, googleClientId?: string }} AuthConfig */

/** @type {AuthConfig | null} */
let authConfig = null;

/**
 * Fetch public Google auth settings from the project server.
 * @returns {Promise<AuthConfig>}
 */
export async function fetchAuthConfig() {
  if (authConfig) {
    return authConfig;
  }

  try {
    const response = await fetch(AUTH_CONFIG_URL);

    if (!response.ok) {
      authConfig = { googleAuthEnabled: false };
      return authConfig;
    }

    authConfig = /** @type {AuthConfig} */ (await response.json());
    return authConfig;
  } catch {
    authConfig = { googleAuthEnabled: false };
    return authConfig;
  }
}

/**
 * Load the Google Identity Services client script.
 * @returns {Promise<void>}
 */
export function loadGoogleScript() {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) {
      resolve(undefined);
      return;
    }

    const existing = document.querySelector(`script[src^="https://accounts.google.com/gsi/client"]`);

    if (existing) {
      existing.addEventListener('load', () => resolve(undefined), { once: true });
      existing.addEventListener(
        'error',
        () => reject(new Error('Could not load Google Sign-In')),
        { once: true },
      );
      return;
    }

    const script = document.createElement('script');
    script.src = GSI_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(undefined);
    script.onerror = () => reject(new Error('Could not load Google Sign-In'));
    document.head.appendChild(script);
  });
}

/**
 * Exchange a Google ID token for the shared GitHub PAT.
 * @param {string} credential Google ID token.
 * @returns {Promise<{ token: string }>}
 */
export async function exchangeGoogleCredential(credential) {
  const response = await fetch(GOOGLE_AUTH_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ credential }),
  });

  if (!response.ok) {
    let message = 'Sign-in failed. Please try again.';

    try {
      const payload = await response.json();

      if (payload?.statusMessage) {
        message = payload.statusMessage;
      } else if (payload?.message) {
        message = payload.message;
      }
    } catch {
      //
    }

    const error = new Error(message);
    error.status = response.status;
    throw error;
  }

  return response.json();
}

/**
 * Render a Google Sign-In button into the given container.
 * @param {HTMLElement} container Button mount point.
 * @param {string} clientId Google OAuth client ID.
 * @param {(credential: string) => void | Promise<void>} onCredential Called with the ID token.
 */
export async function renderGoogleSignInButton(container, clientId, onCredential) {
  await loadGoogleScript();

  if (!window.google?.accounts?.id) {
    throw new Error('Could not load Google Sign-In');
  }

  window.google.accounts.id.initialize({
    client_id: clientId,
    locale: GOOGLE_LOCALE,
    callback: async (response) => {
      if (response?.credential) {
        await onCredential(response.credential);
      }
    },
  });

  window.google.accounts.id.renderButton(container, {
    theme: 'outline',
    size: 'large',
    width: 320,
    locale: GOOGLE_LOCALE,
  });
}
